import { tailwindColors, TailwindColorValue } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  BalloonIcon,
  BuildingTwoIcon,
  BusThreeIcon,
  CoinIcon,
  CoinThreeIcon,
  DotIcon,
  ExclamationMarkOutlinedIcon,
  StarIcon,
  TelephoneIcon,
  UserFourIcon
} from '@repo/ui/icons'
import Lock from '@repo/ui/lock'
import StatusIndicator from '@repo/ui/status-indicator'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import CountryFlag from '../../../../components/elements/country-flag'
import InformationToolTip from '../../../../components/elements/information-tooltip'
import { DisplayPayments } from '../../../../components/pages/center-ol-payments/utils'
import PlacementSummary from '../../../../components/pages/ols/centers/placement-summary'
import { AccomodationEnum } from '../../../../lib/types/enums/accomodation-type/accomodation-type'
import { centerStatusMapping } from '../../../../lib/types/enums/center/center-status'
import {
  CenterTripBookingDiffusionStatusEnum,
  getCenterTripBookingInfo,
  getCenterTripInfo
} from '../../../../lib/types/enums/center/center-trip-status.enum'
import { CenterOlPaymentTypeEnum } from '../../../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { teflTypeEnumMapping } from '../../../../lib/types/enums/ol/tefl-type.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'

type OlCenterColumn<T = Center> = Column<T> & {
  variant?: {
    role: RoleEnum
    order: number
  }[] // Optional so columns without a role restriction are shown to all
  olTypes?: OLTypeEnum[]
}

type OlCenterColumnsOptions = {
  emptyColumns?: string[]
  olType?: OLTypeEnum
  onTelephoneClick?: (center: Center) => void
  onTeachingHallClick?: (center: Center) => void
}

const olCenterColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  userRoles: RoleEnum[],
  allowCenterLink: boolean,
  options?: Pick<OlCenterColumnsOptions, 'onTelephoneClick' | 'onTeachingHallClick'>
): OlCenterColumn[] => [
  {
    accessorKey: 'informationCompleted',
    header: '',
    cell: ({ row }) => (
      <StatusIndicator status={row.original.informationCompleted ? 'active' : 'inactive'} />
    ),
    variant: [{ role: RoleEnum.ADMIN, order: 1 }]
  },
  {
    accessorKey: 'name',
    width: 180,
    header: t('ol.center.table.header.name'),
    variant: [
      { role: RoleEnum.ADMIN, order: 2 },
      { role: RoleEnum.Ol, order: 1 }
    ],
    cell: ({ row }) => (
      <div className="flex flex-col items-start gap-1.5 py-3">
        <div className="flex flex-row gap-1.5">
          <Badge
            text={
              row.original.journeyPlace?.country.code ? (
                <CountryFlag code={row.original.journeyPlace.country.code} />
              ) : (
                '-'
              )
            }
          />

          {allowCenterLink ? (
            <Badge
              className="h-fit py-2 text-balance break-words"
              text={row.original.name.toLocaleUpperCase()}
              color={tailwindColors.white.active}
              textColor={tailwindColors.primary.DEFAULT}
            />
          ) : (
            <Badge
              className="h-fit py-2 text-balance break-words"
              text={row.original.name.toLocaleUpperCase()}
              color={tailwindColors.white.active}
              textColor={tailwindColors.primary.DEFAULT}
            />
          )}
          {userRoles.includes(RoleEnum.ADMIN) && row.original.locked && (
            <Lock isLocked size={20} className="shrink-0 self-center" />
          )}
        </div>
        <Badge
          className="text-balance h-fit py-2"
          text={CenterProperties.getCenterFormulaName(row.original).join(' , ')}
          color={tailwindColors.white.active}
          textColor={tailwindColors.navyBlue.DEFAULT}
        />
        <Badge
          className="text-balance h-fit py-2"
          text={CenterProperties.getCenterThemeName(row.original).join(' , ')}
          color={tailwindColors.white.active}
          textColor={tailwindColors.blue.DEFAULT}
        />
        {CenterProperties.getCenterOptions(row.original).length > 0 && (
          <Badge
            className="text-balance h-fit py-2"
            text={CenterProperties.getCenterOptions(row.original).join(' , ')}
            color={tailwindColors.white.active}
            textColor={tailwindColors.secondary.DEFAULT}
          />
        )}
        <Badge
          className="text-balance h-fit py-2"
          text={CenterProperties.getMainTripsTransportMean(row.original)}
        />
      </div>
    )
  },
  {
    accessorKey: 'id',
    header: t('ol.center.table.header.date'),
    columnHeaderClassName: 'text-center',
    width: 90,
    variant: [
      { role: RoleEnum.ADMIN, order: 4 },
      { role: RoleEnum.Ol, order: 3 }
    ],
    cell: ({ row }) => (
      <Badge
        className="text-balance h-fit py-2"
        text={CenterProperties.getCenterDates(row.original, { i18n: false })}
      />
    )
  },
  {
    accessorKey: 'olPlacedStudentsNumber',
    width: 80,
    header: t('ol.center.table.placed.students'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 4 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cell: ({ row }) => {
      if (row.original.accomodation === AccomodationEnum.HOST_FAMILY)
        return <Badge variant="success" text={row.original.olPlacedStudentsNumber} />
      return '-'
    }
  },
  {
    accessorKey: 'olAffectedStudentsNumber',
    width: 80,
    header: t('ol.center.table.students'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 4 }],
    olTypes: [OLTypeEnum.RESIDENCE],
    cell: ({ row }) => <Badge variant="success" text={row.original.olAffectedStudentsNumber} />
  },
  {
    accessorKey: 'olUnplacedStudentsNumber',
    width: 80,
    header: t('ol.center.table.unplaced.students'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 5 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cell: ({ row }) => {
      if (row.original.accomodation === AccomodationEnum.HOST_FAMILY)
        return <Badge variant="danger" text={row.original.olUnplacedStudentsNumber} />
      return '-'
    }
  },
  {
    accessorKey: 'unplacedStaffsNumber',
    width: 80,
    header: t('ol.center.table.french.leaders'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 5 }],
    olTypes: [OLTypeEnum.RESIDENCE],
    cell: ({ row }) => (
      <Badge
        variant="danger"
        text={row.original.unplacedStaffsNumber + row.original.placedStaffsNumber}
      />
    )
  },
  {
    accessorKey: 'placedStaffsNumber',
    width: 80,
    header: t('ol.center.table.placed.staffs'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 6 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cell: ({ row }) => {
      if (row.original.accomodation === AccomodationEnum.HOST_FAMILY)
        return <Badge variant="success" text={row.original.placedStaffsNumber} />
      return '-'
    }
  },
  {
    accessorKey: 'unplacedStaffsNumber',
    width: 80,
    header: t('ol.center.table.unplaced.staffs'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 7 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cell: ({ row }) => {
      if (row.original.accomodation === AccomodationEnum.HOST_FAMILY)
        return <Badge variant="danger" text={row.original.unplacedStaffsNumber} />
      return '-'
    }
  },
  {
    accessorKey: 'placedStudentsNumber',
    header: t('ol.center.table.affected.students'),
    variant: [{ role: RoleEnum.ADMIN, order: 4 }],
    centerCell: true,
    cell: ({ row }) => {
      // Sylvie:
      // - display the number of students and French leaders all the time.
      // - The difference for the HF centers is that you precise the number of placed / unplaced.

      const totalStudents = row.original.placedStudentsNumber + row.original.unplacedStudentsNumber

      // if not a host family accomodation, display the total number of students without distinction of placed/unplaced
      if (row.original.accomodation !== AccomodationEnum.HOST_FAMILY) return totalStudents
      return (
        <PlacementSummary
          placed={row.original.placedStudentsNumber}
          notPlaced={row.original.unplacedStudentsNumber}
          total={totalStudents}
        />
      )
    }
  },
  {
    accessorKey: 'placedStaffsNumber',
    header: t('ol.center.table.affected.staffs'),
    variant: [{ role: RoleEnum.ADMIN, order: 6 }],
    centerCell: true,
    cell: ({ row }) => {
      // Sylvie:
      // - display the number of students and French leaders all the time.
      // - The difference for the HF centers is that you precise the number of placed / unplaced.

      const totalStaffs = row.original.placedStaffsNumber + row.original.unplacedStaffsNumber

      // if not a host family accomodation, display the total number of French leaders without distinction of placed/unplaced
      if (row.original.accomodation !== AccomodationEnum.HOST_FAMILY) return totalStaffs
      return (
        <PlacementSummary
          placed={row.original.placedStaffsNumber}
          notPlaced={row.original.unplacedStaffsNumber}
          total={totalStaffs}
        />
      )
    }
  },
  {
    accessorKey: 'numberOfAddedEnrollments',
    width: 80,
    header: t('ol.center.table.update.status'),
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 7 }],
    cell: ({ row }) => (
      <Badge
        text={`${row.original.numberOfAddedEnrollments ?? 0} / ${(row.original.numberOfRemovedEnrollments ?? 0) * -1}`}
        variant="warning"
        size="flexible"
        className="px-2"
      />
    )
  },
  {
    accessorKey: 'numberOfOlEnrollments',
    width: 90,
    header: t('ol.center.table.students.already.distributed.to.presta'),
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 8 }],
    cell: ({ row }) => {
      const enrollmentsDiffusedToOlAt = row.original.enrollmentsDiffusedToOlAt
      return (
        <div className="flex flex-col gap-1 items-center">
          <Badge text={row.original.numberOfOlEnrollments ?? 0} />
          {enrollmentsDiffusedToOlAt && (
            <Badge text={DateFormatter.DDMMYYYY(enrollmentsDiffusedToOlAt)} />
          )}
        </div>
      )
    }
  },
  {
    accessorKey: 'hostFamiliesDiffused',
    header: t('ol.center.table.distribution.of.client.information'),
    width: 80,
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 9 }],
    cell: ({ row }) => {
      const diffused = row.original.hostFamiliesDiffused
      const color: TailwindColorValue = diffused ? 'green' : 'secondary'
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={<DotIcon color={tailwindColors[color].DEFAULT} />}
              color={tailwindColors[color].light.DEFAULT}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty w-24"
          >
            {diffused
              ? t('ol.center.table.host.family.diffused')
              : t('ol.center.table.host.family.not.diffused')}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'tripBookingDiffused',
    header: t('ol.center.table.travel.info'),
    width: 90,
    variant: [
      { role: RoleEnum.ADMIN, order: 10 },
      { role: RoleEnum.Ol, order: 8 }
    ],
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    cell: ({ row }) => {
      const { label, bgColor, textColor, showModifiedIcon, showDiffusedAt } =
        getCenterTripBookingInfo(row.original)
      const isPendingFirstDiffusion =
        row.original.tripBookingDiffusionStatus ===
        CenterTripBookingDiffusionStatusEnum.PENDING_FIRST_DIFFUSION

      return (
        <InformationToolTip
          disabled={userRoles.includes(RoleEnum.Ol) && isPendingFirstDiffusion}
          variant={isPendingFirstDiffusion ? 'warning' : 'secondary'}
          contentVariant="secondary"
          icon={
            <div className="flex items-center gap-1 flex-col">
              <Badge
                text={
                  showModifiedIcon ? (
                    <ExclamationMarkOutlinedIcon color={textColor} />
                  ) : (
                    <DotIcon color={textColor} />
                  )
                }
                color={bgColor}
                className="h-8"
              />
              {showDiffusedAt && row.original.tripBookingDiffusedAt && (
                <Badge
                  textColor={textColor}
                  color={bgColor}
                  text={DateFormatter.DDMMYYYY(row.original.tripBookingDiffusedAt)}
                />
              )}
            </div>
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty text-center w-40"
          >
            {t(label)}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'tripsStatus',
    width: 80,
    header: t('ol.center.table.travel.transfer.details'),
    variant: [
      { role: RoleEnum.ADMIN, order: 11 },
      { role: RoleEnum.Ol, order: 9 }
    ],
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    cell: ({ row }) => {
      const { bgColor, textColor } = getCenterTripInfo(row.original)
      return <Badge text={<BusThreeIcon color={textColor} />} color={bgColor} className="h-8" />
    }
  },
  {
    accessorKey: 'activitiesStatus',
    width: 80,
    header: t('ol.center.table.activity.program'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 12 },
      { role: RoleEnum.Ol, order: 10 }
    ],
    cell: ({ row }) => {
      const activityStatus = row.original.activitiesStatus
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={<BalloonIcon color={centerStatusMapping[activityStatus].color} />}
              color={centerStatusMapping[activityStatus].background}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {t(centerStatusMapping[activityStatus].activityLabel)}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'costsStatus',
    header: t('ol.center.table.cost.of.center'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 16 },
      { role: RoleEnum.Ol, order: 11 }
    ],
    olTypes: [OLTypeEnum.LEC],
    cell: ({ row }) => {
      const costsStatus = row.original.costsStatus

      if (row.original.ol?.type && ![OLTypeEnum.LEC].includes(row.original.ol.type)) return '-'

      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={
                <CoinIcon
                  filled
                  color={centerStatusMapping[costsStatus].color}
                  color2={centerStatusMapping[costsStatus].background}
                />
              }
              color={centerStatusMapping[costsStatus].background}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {t(centerStatusMapping[costsStatus].activityLabel)}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: '',
    header: t('ol.center.table.advance.money.transfer'),
    columnHeaderClassName: 'min-w-[100px] text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 17 },
      { role: RoleEnum.Ol, order: 16 }
    ],
    cell: ({ row }) => (
      <DisplayPayments
        type={CenterOlPaymentTypeEnum.ADVANCE}
        payments={row.original.centerBankPayments}
      />
    )
  },
  {
    accessorKey: '',
    header: t('ol.center.table.before.stay.money.transfer'),
    columnHeaderClassName: 'min-w-[100px] text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 18 },
      { role: RoleEnum.Ol, order: 17 }
    ],
    cell: ({ row }) => (
      <DisplayPayments
        type={CenterOlPaymentTypeEnum.COST}
        payments={row.original.centerBankPayments}
      />
    )
  },
  {
    accessorKey: 'finalCostsStatus',
    header: t('ol.center.table.final.cost'),
    columnHeaderClassName: 'min-w-[100px] text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 19 },
      { role: RoleEnum.Ol, order: 15 }
    ],
    olTypes: [OLTypeEnum.LEC],
    cell: ({ row }) => {
      const finalCostsStatus = row.original.finalCostsStatus
      const returnDate = row.original.returnDate
      const isAfterReturnDate = returnDate ? new Date() > new Date(returnDate) : false

      if (row.original.ol?.type && ![OLTypeEnum.LEC].includes(row.original.ol.type)) return '-'
      if (!isAfterReturnDate) return '-'

      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              text={
                <CoinThreeIcon
                  filled
                  color={centerStatusMapping[finalCostsStatus].color}
                  color2={centerStatusMapping[finalCostsStatus].background}
                />
              }
              color={centerStatusMapping[finalCostsStatus].background}
              className="h-8"
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {t(centerStatusMapping[finalCostsStatus].label)}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: '',
    header: t('ol.center.table.balance.money.transfer'),
    columnHeaderClassName: 'min-w-[100px] text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 20 },
      { role: RoleEnum.Ol, order: 18 }
    ],
    cell: ({ row }) => (
      <DisplayPayments
        type={CenterOlPaymentTypeEnum.FINAL}
        payments={row.original.centerBankPayments}
      />
    )
  },
  {
    accessorKey: 'mobileNumberForFrenchLeaderCompleted',
    width: 80,
    header: t('ol.center.table.mobile.number.for.FL'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 14 }],
    cell: ({ row }) => {
      if (row.original.ol?.type && [OLTypeEnum.RESIDENCE].includes(row.original.ol.type))
        return '-'
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              onClick={() => options?.onTelephoneClick?.(row.original)}
              className="h-8"
              variant={row.original.mobileNumberForFrenchLeaderCompleted ? 'success' : 'danger'}
              text={
                <TelephoneIcon
                  filled
                  color={
                    row.original.mobileNumberForFrenchLeaderCompleted
                      ? tailwindColors.green.DEFAULT
                      : tailwindColors.red.DEFAULT
                  }
                />
              }
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {row.original.mobileNumberForFrenchLeaderCompleted
              ? t('ol.center.table.mobile.number.for.FL.allocated.tooltip')
              : t('ol.center.table.mobile.number.for.FL.to.allocate.tooltip')}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'mobileNumberForFrenchLeaderCompleted',
    header: t('ol.center.table.mobile.number.for.FL'),
    columnHeaderClassName: 'min-w-[130px] text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 13 }],
    cell: ({ row }) => {
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              className="h-8"
              variant={row.original.mobileNumberForFrenchLeaderCompleted ? 'success' : 'danger'}
              text={
                <TelephoneIcon
                  filled
                  color={
                    row.original.mobileNumberForFrenchLeaderCompleted
                      ? tailwindColors.green.DEFAULT
                      : tailwindColors.red.DEFAULT
                  }
                />
              }
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {row.original.mobileNumberForFrenchLeaderCompleted
              ? t('ol.center.table.mobile.number.for.FL.allocated.tooltip')
              : t('ol.center.table.mobile.number.for.FL.to.allocate.tooltip')}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'centerTefls',
    header: 'TEFL',
    columnHeaderClassName: 'min-w-[100px] text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 14 },
      { role: RoleEnum.Ol, order: 12 }
    ],
    olTypes: [OLTypeEnum.LEC],
    cell: ({ row }) => {
      if (row.original.ol?.type && ![OLTypeEnum.LEC].includes(row.original.ol.type)) return '-'
      return (
        <div className="flex flex-wrap gap-2 w-24">
          {row.original.centerTefls.map((centerTefl) => {
            const assigned = !!centerTefl.teflId
            const color: TailwindColorValue = assigned ? 'green' : 'red'
            return (
              <InformationToolTip
                key={centerTefl.id}
                contentVariant="secondary"
                icon={
                  <Badge
                    key={centerTefl.id}
                    icon={<UserFourIcon light color={tailwindColors[color].DEFAULT} />}
                    color={tailwindColors[color].light.DEFAULT}
                    textColor={tailwindColors[color].DEFAULT}
                  />
                }
              >
                <Typography
                  variant="label-medium"
                  color={tailwindColors.black.DEFAULT}
                  className="text-pretty"
                >
                  {t(teflTypeEnumMapping[centerTefl.qualification])}
                </Typography>
              </InformationToolTip>
            )
          })}
        </div>
      )
    }
  },
  {
    accessorKey: 'teachingHallsCompleted',
    width: 80,
    header: t('ol.center.table.teaching.hall'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 15 }],
    cell: ({ row }) => {
      if (row.original.ol?.type && [OLTypeEnum.RESIDENCE].includes(row.original.ol.type)) return
      const color: TailwindColorValue = row.original.teachingHallsCompleted ? 'green' : 'red'
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              icon={<BuildingTwoIcon light color={tailwindColors[color].DEFAULT} />}
              color={tailwindColors[color].light.DEFAULT}
              textColor={tailwindColors[color].DEFAULT}
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {row.original.teachingHallsCompleted
              ? t('ol.center.table.teaching.hall.assign.tooltip')
              : t('ol.center.table.teaching.hall.to.assign.tooltip')}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'teachingHallsCompleted',
    width: 80,
    header: t('ol.center.table.teaching.hall'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 13 }],
    olTypes: [OLTypeEnum.LEC],
    cell: ({ row }) => {
      if (row.original.ol?.type && [OLTypeEnum.RESIDENCE].includes(row.original.ol.type)) return
      const color: TailwindColorValue = row.original.teachingHallsCompleted ? 'green' : 'red'
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            <Badge
              onClick={() => options?.onTeachingHallClick?.(row.original)}
              icon={<BuildingTwoIcon light color={tailwindColors[color].DEFAULT} />}
              color={tailwindColors[color].light.DEFAULT}
              textColor={tailwindColors[color].DEFAULT}
            />
          }
        >
          <Typography
            variant="label-medium"
            color={tailwindColors.black.DEFAULT}
            className="text-pretty"
          >
            {row.original.teachingHallsCompleted
              ? t('ol.center.table.teaching.hall.assign.tooltip')
              : t('ol.center.table.teaching.hall.to.assign.tooltip')}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    // "Diffusion des appréciations de fin de séjour": whether LEC shared the centre's ratings with the provider
    accessorKey: 'endOfStayAppreciationsDiffused',
    width: 120,
    header: t('ol.center.table.students.evaluation'),
    columnHeaderClassName: 'text-center',
    centerCell: true,
    variant: [
      { role: RoleEnum.ADMIN, order: 21 },
      { role: RoleEnum.Ol, order: 19 }
    ],
    cell: ({ row }) => {
      const { endOfStayAppreciationsDiffused, endOfStayAppreciationsDiffusedAt } = row.original
      const color: TailwindColorValue = endOfStayAppreciationsDiffused ? 'green' : 'grey'
      return (
        <div className="flex flex-col gap-1 items-center">
          <Badge
            icon={<StarIcon color={tailwindColors[color].DEFAULT} />}
            color={tailwindColors[color].light.DEFAULT}
          />
          {endOfStayAppreciationsDiffused && endOfStayAppreciationsDiffusedAt && (
            <Badge text={DateFormatter.DDMMYYYY(endOfStayAppreciationsDiffusedAt)} />
          )}
        </div>
      )
    }
  }
]

export const getOlCenterColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  userRoles: RoleEnum[],
  allowCenterLink: boolean = true,
  options?: OlCenterColumnsOptions
): Column<Center>[] => {
  return olCenterColumns(t, userRoles, allowCenterLink, options)
    .filter(
      (column) =>
        !column.variant || column.variant.some((variant) => userRoles.includes(variant.role))
    )
    .filter(
      (column) =>
        !options?.olType ||
        !column.olTypes ||
        (column.olTypes && column.olTypes.includes(options?.olType))
    )
    .sort((a, b) => {
      const aOrder = a.variant?.find((v) => userRoles.includes(v.role))?.order ?? 0
      const bOrder = b.variant?.find((v) => userRoles.includes(v.role))?.order ?? 0
      return aOrder - bOrder
    })
    .map((column) => {
      if (options?.emptyColumns && options.emptyColumns.includes(column.accessorKey as string))
        return { ...column, cell: () => {} }
      return column
    })
}
